import { Button, ButtonGroup, Colors, Spinner, Tag } from "@blueprintjs/core";
import isArray from "lodash/isArray";
import isEmpty from "lodash/isEmpty";
import { IFGCustomInputProps, useFGContext } from "nsitools-react";
import * as React from "react";
import { useQuery } from "react-query";
import styled from "styled-components";

import { ApprenantSelectorDialog } from ".";
import { ClearButton, FGReadOnlyInput } from "../..";
import { ApprenantSelectorApi } from "../../../api";
import { ERoutes } from "../../../AppRouter";
import { useAuth } from "../../../contexts";
import { useApiService } from "../../../hooks";

const InputContainer = styled.div`
  padding: 0.4rem 0 0 0.6rem;
`;

const StyledTag = styled(Tag)`
  & a {
    color: ${Colors.WHITE};
  }
`;

const NamesContainer = styled.div`
  > * + * {
    margin-left: 5px;
  }
`;

export interface IApprenantSelectorFieldProps extends Omit<IFGCustomInputProps, "children"> {
  parentApprenantIds?: number[];
  disabled?: boolean;
  allowMultipleSelection?: boolean;
  initialSearch?: boolean;
  ref?: React.Ref<HTMLDivElement>;
}

export const ApprenantSelectorField: React.FunctionComponent<IApprenantSelectorFieldProps> = ({
  name,
  parentApprenantIds = [],
  label = null,
  disabled = false,
  className,
  allowMultipleSelection = false,
  initialSearch = true,
  ref,
  ...fgCustomInputProps
}) => {
  const { hasPermission } = useAuth();
  const ctx = useFGContext();
  const api = useApiService(ApprenantSelectorApi);

  const hasValue = React.useMemo(
    () =>
      (allowMultipleSelection && !isEmpty(ctx?.formik?.values?.[name])) ||
      (!allowMultipleSelection && !!ctx?.formik?.values?.[name]),
    [allowMultipleSelection, ctx?.formik?.values, name]
  );
  const [selectorDialogOpen, setSelectorDialogOpen] = React.useState(false);
  const [currentSelectedApprenantIds, setSelectedApprenantIds] = React.useState<number[]>([]);

  const { data: displayNames, isFetching: loadingDisplayNames } = useQuery(
    ["apprenant-display-names", currentSelectedApprenantIds],
    async () => api.apprenantSelectorGetDisplayNames({ request_body: currentSelectedApprenantIds })
  );

  const clearSelectedApprenant = React.useCallback(() => {
    ctx?.formik?.setFieldValue(name, allowMultipleSelection ? [] : null, true);
    setSelectedApprenantIds([]);
  }, [allowMultipleSelection, ctx?.formik, name]);

  React.useEffect(() => {
    if (ctx?.formik?.values?.[name]) {
      const value = ctx?.formik?.values?.[name];
      setSelectedApprenantIds(isArray(value) ? value : [value]);
    } else {
      setSelectedApprenantIds([]);
    }
  }, [ctx?.formik?.values, name]);

  const onApprenantsSelected = React.useCallback(
    (selectedApprenantIds?: number[]) => {
      if (selectedApprenantIds?.length > 0) {
        ctx?.formik?.setFieldTouched(name);
        ctx?.formik?.setFieldValue(name, allowMultipleSelection ? selectedApprenantIds : selectedApprenantIds[0], true);
        setSelectedApprenantIds(selectedApprenantIds);
      }
    },
    [allowMultipleSelection, ctx?.formik, name]
  );

  const selectorProps = React.useMemo(
    () => ({
      excludedApprenantIds: parentApprenantIds,
      allowMultipleSelection,
      preSelectedIds: currentSelectedApprenantIds
    }),
    [allowMultipleSelection, parentApprenantIds, currentSelectedApprenantIds]
  );

  const openApprenantSelector = React.useCallback(() => {
    setSelectorDialogOpen(true);
  }, []);

  const getEditRoute = React.useCallback(
    (id: number) =>
      `#${ERoutes.apprenant}/${id}/signaletique/${hasPermission("APPRENANT", "RW", "SIGNALETIQUE") ? "edit" : "view"}`,
    [hasPermission]
  );

  const removeApprenantId = React.useCallback(
    (apprenantId: number) => {
      const newApprenantIds = [...currentSelectedApprenantIds];
      newApprenantIds.splice(currentSelectedApprenantIds.indexOf(apprenantId), 1);
      ctx?.formik?.setFieldTouched(name);
      ctx?.formik?.setFieldValue(name, newApprenantIds, true);
      setSelectedApprenantIds(newApprenantIds);
    },
    [ctx?.formik, currentSelectedApprenantIds, name]
  );

  const getApprenantLink = React.useCallback(
    (id: number, name: string, tags: boolean) => {
      const link = hasPermission("APPRENANT", "R", "SIGNALETIQUE") ? (
        <a href={getEditRoute(id)} target="_blank" rel="noreferrer" tabIndex={-1} key={id}>
          {name}
        </a>
      ) : (
        name
      );
      return tags ? (
        <StyledTag key={id} onRemove={() => removeApprenantId(id)}>
          {link}
        </StyledTag>
      ) : (
        link
      );
    },
    [getEditRoute, hasPermission, removeApprenantId]
  );

  const onClosed = React.useCallback(
    (selectedApprenantIds?: number[]) => {
      onApprenantsSelected(selectedApprenantIds);
      setSelectorDialogOpen(false);
    },
    [onApprenantsSelected]
  );

  return (
    <>
      <FGReadOnlyInput
        name={name}
        label={label}
        readonly={!ctx.editMode}
        leftElement={
          <InputContainer>
            {loadingDisplayNames ? (
              <Spinner size={16} />
            ) : (
              <NamesContainer>
                {displayNames.map(dn => getApprenantLink(dn.id, dn.text, allowMultipleSelection))}
              </NamesContainer>
            )}
          </InputContainer>
        }
        rightElement={
          <ButtonGroup>
            {hasValue && (
              <ClearButton onClick={clearSelectedApprenant} tabIndex={-1} disabled={disabled || !ctx.editMode} />
            )}
            <Button
              icon="people"
              minimal={true}
              onClick={openApprenantSelector}
              disabled={disabled || !ctx.editMode}
              tabIndex={-1}
            />
          </ButtonGroup>
        }
        {...fgCustomInputProps}
      />
      <ApprenantSelectorDialog
        onClosed={onClosed}
        onApprenantSelected={onClosed}
        selectorDialogOpen={selectorDialogOpen}
        initialSearch={initialSearch}
        {...selectorProps}
      />
    </>
  );
};
